import { useEffect, useRef, useState } from 'react';

export type TopLoadingBarProps = {
  isLoading: boolean;
  progress?: number;
  height?: number;
  className?: string;
  onComplete?: () => void;
};

export function ContainerTopLoading({
  isLoading,
  progress,
  height = 4,
  className = '',
  onComplete,
}: TopLoadingBarProps) {
  const [internalProgress, setInternalProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset progress when loading starts
  useEffect(() => {
    if (isLoading) {
      setInternalProgress(0);
      setVisible(true);
    }
  }, [isLoading]);

  // Handle automatic progress increment
  useEffect(() => {
    if (isLoading && progress === undefined) {
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Set up timer to increment progress by 10% each second, up to 90%
      timerRef.current = setInterval(() => {
        setInternalProgress((prev) => {
          if (prev < 90) {
            return prev + 10;
          }
          clearInterval(timerRef.current as NodeJS.Timeout);
          return 90;
        });
      }, 400);
    } else if (!isLoading && visible) {
      // When loading completes, clear timer and go to 100%
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Complete the progress bar
      setInternalProgress(100);

      // After animation completes, hide the bar
      const timeout = setTimeout(() => {
        setVisible(false);
        setInternalProgress(0);
        if (onComplete) onComplete();
      }, 400); // Slightly longer than the transition time

      return () => clearTimeout(timeout);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isLoading, progress, visible, onComplete]);

  // Handle external progress updates
  useEffect(() => {
    if (progress !== undefined && isLoading) {
      setInternalProgress(Math.min(progress, 90));
    }
  }, [progress, isLoading]);

  return (
    <div
      ref={containerRef}
      className={`absolute left-0 top-0 w-full overflow-hidden ${className}`}
      style={{ height: visible ? height : 0 }}
    >
      <div
        className="absolute left-0 top-0 h-full bg-primary-500 transition-all duration-300 ease-out"
        style={{
          width: `${internalProgress}%`,
          opacity: visible ? 1 : 0,
        }}
      />
    </div>
  );
}
